import {
  computed,
  ref,
  type ComponentPublicInstance,
} from 'vue'

import type { FineUploaderModalUploadHelpers } from '@/composables/current-student-advanced-standing-form/fineUploaderModalUploadHelpers'
import { useUploadAttachmentState } from '@/composables/current-student-advanced-standing-form/useUploadAttachmentState'
import {
  externalSubjectCodeInputAllowed,
  initialExternalSubjectModalFormState,
} from '@/models/current-student-advanced-standing-form/externalSubjectModal'
import {
  clearModalAlert,
  showModalError,
  type AlertTone,
} from '@/models/current-student-advanced-standing-form/modalAlert'
import type {
  Attachment,
  ExternalCourse,
  ExternalSubject,
  FineUploaderUpload,
} from '@/models/current-student-advanced-standing-form/api'

type UseExternalSubjectModalControllerOptions = {
  maxAttachments: number
  uploadHelpers: FineUploaderModalUploadHelpers
}

export function useExternalSubjectModalController({
  maxAttachments,
  uploadHelpers,
}: UseExternalSubjectModalControllerOptions) {
  const externalSubjectModalVisible = ref(false)
  const externalSubjectSaving = ref(false)
  const externalSubjectModalError = ref('')
  const externalSubjectModalAlertTone = ref<AlertTone>('danger')
  const externalSubjectFormRef = ref<HTMLFormElement | null>(null)
  const currentExternalSubject = ref<Partial<ExternalSubject> | null>(null)
  const externalSubjectCode = ref('')
  const externalSubjectName = ref('')
  const externalSubjectYear = ref('')
  const externalSubjectLevel = ref('')
  const externalSubjectUrl = ref('')
  const subjectAttachmentDeleteIds = ref<number[]>([])
  const subjectNewAttachments = ref<FineUploaderUpload[]>([])
  const subjectUploadsInProgress = ref(0)
  const subjectUploadDropActive = ref(false)
  const subjectUploadAlertMessage = ref('')

  const currentSubjectAttachments = computed(() => {
    return currentExternalSubject.value?.external_subject_attachments ?? []
  })

  const {
    uploadItemLimit: subjectUploadItemLimit,
    activeNewUploadCount: activeSubjectNewUploadCount,
    canUploadAttachment: canUploadSubjectAttachment,
    visibleNewAttachments: visibleSubjectNewAttachments,
    showFineUploader: showSubjectFineUploader,
  } = useUploadAttachmentState({
    savedAttachments: currentSubjectAttachments,
    deletedAttachmentIds: subjectAttachmentDeleteIds,
    newAttachments: subjectNewAttachments,
    maxAttachments,
  })

  function openAddExternalSubjectModal(course: ExternalCourse): void {
    openExternalSubjectModal({ external_course_id: course.external_course_id })
  }

  function openEditExternalSubjectModal(subject: ExternalSubject, course: ExternalCourse): void {
    openExternalSubjectModal({
      ...subject,
      external_course_id: course.external_course_id,
    })
  }

  function openExternalSubjectModal(subject: Partial<ExternalSubject>): void {
    const formState = initialExternalSubjectModalFormState(subject)

    currentExternalSubject.value = formState.currentExternalSubject
    clearModalAlert(externalSubjectModalError, externalSubjectModalAlertTone)
    externalSubjectCode.value = formState.externalSubjectCode
    externalSubjectName.value = formState.externalSubjectName
    externalSubjectLevel.value = formState.externalSubjectLevel
    externalSubjectYear.value = formState.externalSubjectYear
    externalSubjectUrl.value = formState.externalSubjectUrl
    subjectAttachmentDeleteIds.value = []
    uploadHelpers.revokeUploadThumbnailsFor(subjectNewAttachments.value)
    subjectNewAttachments.value = []
    subjectUploadsInProgress.value = 0
    externalSubjectModalVisible.value = true
  }

  function closeExternalSubjectModal(): void {
    uploadHelpers.revokeUploadThumbnailsFor(subjectNewAttachments.value)
    externalSubjectModalVisible.value = false
  }

  function setExternalSubjectFormRef(element: Element | ComponentPublicInstance | null): void {
    externalSubjectFormRef.value = element instanceof HTMLFormElement ? element : null
  }

  function onExternalSubjectCodeBeforeInput(event: Event): void {
    const inputEvent = event as InputEvent
    if (inputEvent.data == null) return

    if (!externalSubjectCodeInputAllowed(inputEvent.data)) {
      event.preventDefault()
    }
  }

  function closeSubjectUploadAlert(): void {
    uploadHelpers.closeFineUploaderAlert('#subjectAttachmentFineUploader', subjectUploadAlertMessage)
  }

  function onSubjectUploadDragEnter(event: DragEvent): void {
    uploadHelpers.onUploadDragEnter(event, subjectUploadDropActive)
  }

  function onSubjectUploadDragOver(event: DragEvent): void {
    uploadHelpers.onUploadDragOver(event, subjectUploadDropActive)
  }

  function onSubjectUploadDragLeave(event: DragEvent): void {
    uploadHelpers.onUploadDragLeave(event, subjectUploadDropActive)
  }

  async function onSubjectUploadDrop(event: DragEvent): Promise<void> {
    await uploadHelpers.onUploadDrop(event, subjectUploadDropActive, uploadSubjectAttachmentFiles)
  }

  async function onSubjectAttachmentSelected(event: Event): Promise<void> {
    await uploadHelpers.onAttachmentSelected(event, uploadSubjectAttachmentFiles)
  }

  async function uploadSubjectAttachmentFiles(files: File[]): Promise<void> {
    if (files.length === 0) return

    if (activeSubjectNewUploadCount.value + files.length > subjectUploadItemLimit.value) {
      await showSubjectUploadLimitAlert(files.length)
      return
    }

    for (const file of files) {
      await uploadSubjectAttachmentFile(file)
    }
  }

  async function showSubjectUploadLimitAlert(addedItems = 1): Promise<void> {
    await uploadHelpers.showUploadLimitAlert(
      '#subjectAttachmentFineUploader',
      subjectUploadAlertMessage,
      activeSubjectNewUploadCount.value,
      subjectUploadItemLimit.value,
      addedItems,
    )
  }

  async function uploadSubjectAttachmentFile(file: File): Promise<void> {
    if (!canUploadSubjectAttachment.value) {
      await showSubjectUploadLimitAlert()
      return
    }

    await uploadHelpers.uploadAttachment(file, subjectNewAttachments, subjectUploadsInProgress, (message) => {
      showModalError(externalSubjectModalError, externalSubjectModalAlertTone, message)
    })
  }

  function stageSubjectAttachmentDelete(attachment: Attachment): void {
    if (!subjectAttachmentDeleteIds.value.includes(attachment.attachment_id)) {
      subjectAttachmentDeleteIds.value.push(attachment.attachment_id)
    }
  }

  async function deleteStagedSubjectAttachment(attachment: FineUploaderUpload): Promise<void> {
    await uploadHelpers.deleteStagedUpload(attachment, (message) => {
      showModalError(externalSubjectModalError, externalSubjectModalAlertTone, message)
    })
  }

  async function retrySubjectAttachment(attachment: FineUploaderUpload): Promise<void> {
    await uploadHelpers.retryUploadAttachment(attachment, subjectUploadsInProgress, (message) => {
      showModalError(externalSubjectModalError, externalSubjectModalAlertTone, message)
    })
  }

  return {
    externalSubjectModalVisible,
    externalSubjectSaving,
    externalSubjectModalError,
    externalSubjectModalAlertTone,
    externalSubjectFormRef,
    currentExternalSubject,
    externalSubjectCode,
    externalSubjectName,
    externalSubjectYear,
    externalSubjectLevel,
    externalSubjectUrl,
    subjectAttachmentDeleteIds,
    subjectNewAttachments,
    subjectUploadsInProgress,
    subjectUploadDropActive,
    subjectUploadAlertMessage,
    currentSubjectAttachments,
    visibleSubjectNewAttachments,
    showSubjectFineUploader,
    openAddExternalSubjectModal,
    openEditExternalSubjectModal,
    openExternalSubjectModal,
    closeExternalSubjectModal,
    setExternalSubjectFormRef,
    onExternalSubjectCodeBeforeInput,
    closeSubjectUploadAlert,
    onSubjectUploadDragEnter,
    onSubjectUploadDragOver,
    onSubjectUploadDragLeave,
    onSubjectUploadDrop,
    onSubjectAttachmentSelected,
    uploadSubjectAttachmentFiles,
    showSubjectUploadLimitAlert,
    uploadSubjectAttachmentFile,
    stageSubjectAttachmentDelete,
    deleteStagedSubjectAttachment,
    retrySubjectAttachment,
  }
}
